// Swipe-down-to-close for bottom sheets (phones only).
//
// Drag starts from the top 80px of the sheet (handle + header). It never intercepts taps
// on controls, and never starts while the sheet itself is scrolled, so a sheet that
// scrolls as a whole can still be scrolled back up. Dragging > 80px or a fast flick
// closes; shorter drags spring back. The transform is set with !important because some
// sheets (.flyout-panel.is-open) pin their transform that way.

const MOBILE = '(max-width: 768px)';
const DRAG_ZONE_PX = 80;
const CLOSE_DISTANCE_PX = 80;
const CLOSE_VELOCITY = 0.35; // px per ms
const IGNORE = 'button, a, input, select, textarea, label, summary, [role="button"], [onclick], .csp-finish-chip';

const attached = new WeakSet<HTMLElement>();

export function addSwipeToClose(sheetEl: HTMLElement | null, closeFn: () => void): void {
    if (!sheetEl || attached.has(sheetEl)) return;
    attached.add(sheetEl);

    let startY = 0;
    let startT = 0;
    let dy = 0;
    let active = false;

    const setTransform = (v: string) => sheetEl.style.setProperty('transform', v, 'important');
    const clearInline = () => {
        sheetEl.style.removeProperty('transform');
        sheetEl.style.removeProperty('transition');
    };

    sheetEl.addEventListener('touchstart', (e: TouchEvent) => {
        if (!window.matchMedia(MOBILE).matches) return;
        const target = e.target as Element | null;
        if (target && target.closest(IGNORE)) return;
        if (sheetEl.scrollTop > 0) return; // let the user scroll back up first
        const rect = sheetEl.getBoundingClientRect();
        const t = e.touches[0];
        if (t.clientY - rect.top > DRAG_ZONE_PX) return;

        startY = t.clientY;
        startT = Date.now();
        dy = 0;
        active = true;
        sheetEl.style.setProperty('transition', 'none', 'important');
        // Non-passive so the browser does not commit to a scroll before touchmove runs.
        e.preventDefault();
    }, { passive: false });

    sheetEl.addEventListener('touchmove', (e: TouchEvent) => {
        if (!active) return;
        dy = Math.max(0, e.touches[0].clientY - startY);
        setTransform('translateY(' + dy + 'px)');
        e.preventDefault();
    }, { passive: false });

    const onEnd = () => {
        if (!active) return;
        active = false;
        const vel = dy / Math.max(1, Date.now() - startT);
        if (dy > CLOSE_DISTANCE_PX || vel > CLOSE_VELOCITY) {
            sheetEl.style.setProperty('transition', 'transform 0.22s ease-in', 'important');
            setTransform('translateY(120%)');
            setTimeout(() => {
                closeFn();
                // Keep the sheet off-screen for the frame in which closeFn removes is-open.
                requestAnimationFrame(clearInline);
            }, 210);
        } else {
            sheetEl.style.setProperty('transition', 'transform 0.28s cubic-bezier(.22,.68,0,1.2)', 'important');
            setTransform('translateY(0)');
            setTimeout(clearInline, 290);
        }
    };

    sheetEl.addEventListener('touchend', onEnd, { passive: true });
    sheetEl.addEventListener('touchcancel', () => {
        if (!active) return;
        active = false;
        clearInline();
    }, { passive: true });
}
